import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCalendarEvents, CalendarEvent } from '../../hooks/useCalendarEvents';
import { usePlanPermissions } from '../../hooks/usePlanPermissions';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import { useCards } from '../../hooks/useCards';
import { useAccounts } from '../../hooks/useAccounts';
import { CalendarMonthView } from './components/CalendarMonthView';
import { CalendarListView } from './components/CalendarListView';
import { EventModal } from './components/EventModal';
import { EventDetailModal } from './components/EventDetailModal';
import { UpgradeModal } from '../../components/feature/UpgradeModal';
import { CardPaymentModal, CardPaymentParams } from '../cartoes/components/CardPaymentModal';

type ViewMode = 'month' | 'list';

const MONTH_NAMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

const FILTER_OPTIONS = [
  { key: 'all', label: 'Todos' },
  { key: 'income', label: 'Entradas' },
  { key: 'expense', label: 'Saídas' },
  { key: 'card', label: 'Cartões' },
  { key: 'manual', label: 'Manuais' },
  { key: 'goal_deadline', label: 'Metas' },
];

const INCOME_TYPES = new Set(['income_expected', 'income_received']);
const EXPENSE_TYPES = new Set(['bill_due', 'recurring_transaction', 'installment', 'payment_reminder']);
const CARD_TYPES = new Set(['card_due', 'card_closing']);

const CalendarioPage = () => {
  const today = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const [year, setYear] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const d = new Date(dateParam + 'T00:00:00');
      if (!isNaN(d.getTime())) return d.getFullYear();
    }
    return today.getFullYear();
  });
  const [month, setMonth] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const d = new Date(dateParam + 'T00:00:00');
      if (!isNaN(d.getTime())) return d.getMonth() + 1;
    }
    return today.getMonth() + 1;
  });
  const [view, setView] = useState<ViewMode>('month');
  const [filter, setFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [detailEvent, setDetailEvent] = useState<CalendarEvent | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [syncFeedback, setSyncFeedback] = useState<string | null>(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [showGCalPanel, setShowGCalPanel] = useState(false);
  const [showAccountStrip, setShowAccountStrip] = useState(false);

  // ── Auto-open event from URL params (notification click) ──
  const pendingOpenIdRef = useRef<string | null>(searchParams.get('open_event_id'));

  const perms = usePlanPermissions();
  const {
    allEvents, eventsByDate, loading, error,
    createEvent, updateEvent, deleteEvent, refetchTransactions,
  } = useCalendarEvents(year, month);
  const { status: gcalStatus, connect: gcalConnect, disconnect: gcalDisconnect, syncEvent, syncBulk } = useGoogleCalendar();
  const { cards, getCardStats, payCardInvoice } = useCards();
  const { accounts, totalBalance } = useAccounts();

  const manualEventsThisMonth = allEvents.filter((e) => e.is_manual).length;
  const canCreate = perms.canCreateManualCalendarEvents && (
    perms.maxCalendarEventsPerMonth === null ||
    manualEventsThisMonth < perms.maxCalendarEventsPerMonth
  );

  const filteredEventsByDate = useMemo(() => {
    if (filter === 'all') return eventsByDate;
    const filtered: Record<string, CalendarEvent[]> = {};
    Object.entries(eventsByDate).forEach(([date, events]) => {
      let result: CalendarEvent[];
      if (filter === 'income') result = events.filter((e) => INCOME_TYPES.has(e.event_type));
      else if (filter === 'expense') result = events.filter((e) => EXPENSE_TYPES.has(e.event_type));
      else if (filter === 'card') result = events.filter((e) => CARD_TYPES.has(e.event_type));
      else if (filter === 'manual') result = events.filter((e) => e.is_manual);
      else result = events.filter((e) => e.event_type === filter);
      if (result.length > 0) filtered[date] = result;
    });
    return filtered;
  }, [eventsByDate, filter]);

  // Card payment modal state (triggered from calendar event)
  const [calendarPaymentModal, setCalendarPaymentModal] = useState<{
    open: boolean;
    cardId: string | null;
    cardName: string | null;
    type: 'full' | 'partial';
  }>({ open: false, cardId: null, cardName: null, type: 'full' });
  const [calendarPaymentStats, setCalendarPaymentStats] = useState<any>(null);

  // Toast for calendar actions
  const [actionToast, setActionToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showActionToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setActionToast({ msg, type });
    setTimeout(() => setActionToast(null), 3500);
  }, []);

  // Handle pay card from calendar
  const handlePayCardFromCalendar = useCallback(async (
    cardId: string | null | undefined,
    cardName: string | null | undefined,
    type: 'full' | 'partial'
  ) => {
    const card = cards.find(c => c.id === cardId);
    if (!card) {
      showActionToast('Cartão não encontrado. Vá para Cartões para pagar a fatura.', 'error');
      return;
    }
    const stats = await getCardStats(card);
    setCalendarPaymentStats(stats);
    setCalendarPaymentModal({ open: true, cardId: cardId ?? null, cardName: cardName ?? null, type });
  }, [cards, getCardStats, showActionToast]);

  const handleCalendarPaymentConfirm = async (params: CardPaymentParams) => {
    const result = await payCardInvoice(params);
    if (result.success) {
      showActionToast(result.message);
    }
    return result;
  };

  // Handle goal contribution from calendar — navigate to goals
  const handleGoalContribution = useCallback((_goalId: string | null | undefined, goalName: string | null | undefined) => {
    showActionToast(`Acesse a seção Metas para fazer um aporte em "${goalName ?? 'sua meta'}".`);
  }, [showActionToast]);

  const prevMonth = () => {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else setMonth((m) => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); };

  const handleDayClick = (date: string) => {
    if (!canCreate) { setUpgradeOpen(true); return; }
    setSelectedDate(date); setEditingEvent(null); setModalOpen(true);
  };

  const handleEventClick = (event: CalendarEvent) => {
    setDetailEvent(event); setDetailOpen(true);
  };

  const handleEditFromDetail = (event: CalendarEvent) => {
    setDetailOpen(false);
    setEditingEvent(event);
    setSelectedDate(event.start_date);
    setModalOpen(true);
  };

  const handleAddNew = () => {
    if (!canCreate) { setUpgradeOpen(true); return; }
    const pad = (n: number) => String(n).padStart(2, '0');
    setSelectedDate(`${year}-${pad(month)}-${pad(today.getDate())}`);
    setEditingEvent(null);
    setModalOpen(true);
  };

  const handleSync = async (event: CalendarEvent) => {
    const result = await syncEvent(event);
    return result;
  };

  const handleBulkSync = async () => {
    if (!perms.canAccessCalendarSync) { setUpgradeOpen(true); return; }
    if (!gcalStatus.connected) { setShowGCalPanel(true); return; }
    setBulkSyncing(true);
    const result = await syncBulk(allEvents.filter((e) => !e.google_calendar_event_id));
    setBulkSyncing(false);
    setSyncFeedback(result.message);
    setTimeout(() => setSyncFeedback(null), 4000);
  };

  // When events load and there's a pending event to open, find and open it
  useEffect(() => {
    if (!pendingOpenIdRef.current || loading) return;
    const eventId = pendingOpenIdRef.current;
    const found = allEvents.find((e) => e.id === eventId);
    if (found) {
      pendingOpenIdRef.current = null;
      // Clear URL params so refreshing doesn't re-open
      setSearchParams({}, { replace: true });
      setDetailEvent(found);
      setDetailOpen(true);
    }
  }, [allEvents, loading, setSearchParams]);

  // Stats
  // REGRA: saldo_projetado = saldo_atual + entradas_PENDENTES - saídas_PENDENTES
  // income_received já está contabilizado no saldo_atual (account.current_balance)
  // → NÃO incluir income_received aqui para evitar dupla contagem
  const totalIncome = allEvents
    .filter((e) => e.event_type === 'income_expected')
    .reduce((s, e) => s + (e.amount ?? 0), 0);

  // Despesas pagas (status=pago) já estão no saldo_atual → excluir para evitar dupla contagem
  const totalExpense = allEvents
    .filter((e) => EXPENSE_TYPES.has(e.event_type) || CARD_TYPES.has(e.event_type))
    .filter((e) => e.amount != null)
    .filter((e) => !['pago', 'recebido'].includes(e.status ?? ''))
    .reduce((s, e) => s + (e.amount ?? 0), 0);
  const totalManualEvents = allEvents.filter((e) => e.is_manual).length;

  const formatCurrency = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-700 text-gray-900">Calendário</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">
            Sua agenda financeira completa — entradas, saídas, cartões e lembretes
          </p>
        </div>
        <div className="flex items-center gap-2">
          {perms.canAccessCalendarSync ? (
            <button
              onClick={() => setShowGCalPanel((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[12px] font-500 cursor-pointer whitespace-nowrap transition-colors ${
                gcalStatus.connected
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              <i className={`ri-google-line text-[14px] ${gcalStatus.connected ? 'text-emerald-600' : ''}`} />
              {gcalStatus.connected ? 'Google Conectado' : 'Google Calendar'}
              {gcalStatus.connected && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
            </button>
          ) : (
            <button
              onClick={() => setUpgradeOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-[12px] font-500 text-amber-700 cursor-pointer whitespace-nowrap hover:bg-amber-100 transition-colors"
            >
              <i className="ri-google-line text-[13px]" />
              Google Calendar
              <span className="text-[10px] bg-amber-200 px-1 py-0.5 rounded font-600">PRO</span>
            </button>
          )}
          <button
            onClick={handleAddNew}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 text-white text-[13px] font-600 hover:bg-emerald-600 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-add-line text-[15px]" />
            Novo Evento
          </button>
        </div>
      </div>

      {/* Google Calendar panel */}
      {showGCalPanel && perms.canAccessCalendarSync && (
        <div className="bg-white rounded-2xl border border-gray-100 px-5 py-4">
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              gcalStatus.connected ? 'bg-emerald-50' : 'bg-gray-100'
            }`}>
              <i className={`ri-google-line text-[18px] ${gcalStatus.connected ? 'text-emerald-600' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[14px] font-700 text-gray-800">Google Calendar</p>
                {gcalStatus.connected ? (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-600">
                    Conectado
                  </span>
                ) : (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-500">
                    Não conectado
                  </span>
                )}
              </div>
              {gcalStatus.connected && gcalStatus.email && (
                <p className="text-[12px] text-gray-500 mb-2">{gcalStatus.email}</p>
              )}
              {!gcalStatus.connected && (
                <p className="text-[12px] text-gray-400 mb-3">
                  Conecte sua conta Google para exportar vencimentos, recebimentos, metas e lembretes para o seu Google Calendar.
                </p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {!gcalStatus.connected ? (
                  <button
                    onClick={gcalConnect}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg text-[13px] font-600 hover:bg-emerald-600 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-google-line" />
                    Conectar com Google
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleBulkSync}
                      disabled={bulkSyncing}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg text-[13px] font-600 hover:bg-emerald-600 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                    >
                      {bulkSyncing ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-upload-cloud-line" />}
                      {bulkSyncing ? 'Sincronizando...' : 'Sincronizar eventos do mês'}
                    </button>
                    <button
                      onClick={gcalDisconnect}
                      className="px-3 py-2 text-[12px] font-500 text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg cursor-pointer whitespace-nowrap"
                    >
                      Desconectar
                    </button>
                  </>
                )}
                <button onClick={() => setShowGCalPanel(false)} className="px-3 py-2 text-[12px] text-gray-400 cursor-pointer whitespace-nowrap">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync feedback */}
      {syncFeedback && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-[13px] text-emerald-700">
          <i className="ri-checkbox-circle-line text-emerald-500" />
          {syncFeedback}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
          <p className="text-[11px] text-gray-400 font-500 uppercase tracking-wide">Total</p>
          <p className="text-[22px] font-700 text-gray-900 mt-0.5">{allEvents.length}</p>
          <p className="text-[11px] text-gray-400">eventos em {MONTH_NAMES[month - 1]}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
          <p className="text-[11px] text-gray-400 font-500 uppercase tracking-wide">Entradas</p>
          <p className="text-[22px] font-700 text-emerald-600 mt-0.5">
            {totalIncome > 0 ? `+${totalIncome.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : '—'}
          </p>
          <p className="text-[11px] text-gray-400">pendentes a receber</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
          <p className="text-[11px] text-gray-400 font-500 uppercase tracking-wide">Saídas</p>
          <p className="text-[22px] font-700 text-red-500 mt-0.5">
            {totalExpense > 0 ? `-${totalExpense.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : '—'}
          </p>
          <p className="text-[11px] text-gray-400">pendentes a pagar</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
          <p className="text-[11px] text-gray-400 font-500 uppercase tracking-wide">Manuais</p>
          <p className="text-[22px] font-700 text-gray-700 mt-0.5">{totalManualEvents}</p>
          <p className="text-[11px] text-gray-400">
            {perms.maxCalendarEventsPerMonth !== null
              ? `de ${perms.maxCalendarEventsPerMonth} neste mês`
              : 'ilimitados'}
          </p>
        </div>
      </div>

      {/* Consolidated financial context strip */}
      {accounts.length > 0 && (
        <div className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <i className="ri-wallet-3-line text-gray-400 text-xs" />
            <span className="text-[11px] font-600 text-gray-400 uppercase tracking-wide">
              Situação atual das suas contas
            </span>
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[11px] text-gray-400">{accounts.length} conta{accounts.length !== 1 ? 's' : ''}</span>
            <button
              onClick={() => setShowAccountStrip((v) => !v)}
              title={showAccountStrip ? 'Ocultar resumo' : 'Exibir resumo'}
              className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            >
              <i className={`text-sm ${showAccountStrip ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
            </button>
          </div>
          {showAccountStrip && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
              <div>
                <p className="text-[11px] text-gray-400 font-500 mb-0.5">Saldo atual</p>
                <p className={`text-sm font-700 ${totalBalance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {formatCurrency(totalBalance)}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">soma de todas as contas</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500 mb-0.5">A receber este mês</p>
                <p className="text-sm font-700 text-teal-600">
                  {totalIncome > 0 ? `+ ${formatCurrency(totalIncome)}` : '—'}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">entradas pendentes no calendário</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500 mb-0.5">A pagar este mês</p>
                <p className="text-sm font-700 text-amber-600">
                  {totalExpense > 0 ? `- ${formatCurrency(totalExpense)}` : '—'}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">saídas pendentes no calendário</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500 mb-0.5">Saldo projetado</p>
                {(() => {
                  const projected = totalBalance + totalIncome - totalExpense;
                  return (
                    <>
                      <p className={`text-sm font-700 ${projected >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {formatCurrency(projected)}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">saldo após {MONTH_NAMES[month - 1]}</p>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors">
            <i className="ri-arrow-left-s-line text-[17px]" />
          </button>
          <span className="text-[15px] font-700 text-gray-900 min-w-[160px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors">
            <i className="ri-arrow-right-s-line text-[17px]" />
          </button>
          <button onClick={goToday} className="px-3 py-1.5 rounded-lg border border-gray-200 text-[12px] font-500 text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap">
            Hoje
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {(perms.canAccessAdvancedCalendarFilters ? FILTER_OPTIONS : FILTER_OPTIONS.slice(0, 3)).map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-500 cursor-pointer transition-all whitespace-nowrap ${
                  filter === f.key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('month')}
              className={`w-8 h-7 flex items-center justify-center rounded-md text-[14px] cursor-pointer transition-all ${view === 'month' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              title="Visão mensal"
            >
              <i className="ri-calendar-2-line" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`w-8 h-7 flex items-center justify-center rounded-md text-[14px] cursor-pointer transition-all ${view === 'list' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              title="Visão em lista"
            >
              <i className="ri-list-check-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Limit warning */}
      {perms.maxCalendarEventsPerMonth !== null &&
        manualEventsThisMonth >= perms.maxCalendarEventsPerMonth && (
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-[13px]">
            <i className="ri-information-line text-amber-500 text-[16px] shrink-0" />
            <span className="text-amber-700">
              Você atingiu o limite de {perms.maxCalendarEventsPerMonth} eventos manuais por mês.{' '}
              <button onClick={() => setUpgradeOpen(true)} className="font-600 underline cursor-pointer">
                Fazer upgrade
              </button>{' '}
              para criar eventos ilimitados.
            </span>
          </div>
        )}

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-[13px] text-red-600">{error}</div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {view === 'month' ? (
            <CalendarMonthView
              year={year} month={month}
              eventsByDate={filteredEventsByDate}
              onDayClick={handleDayClick}
              onEventClick={handleEventClick}
            />
          ) : (
            <CalendarListView
              year={year} month={month}
              eventsByDate={filteredEventsByDate}
              onEventClick={handleEventClick}
              onAddDay={handleDayClick}
            />
          )}
        </>
      )}

      {!loading && allEvents.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 flex flex-col items-center justify-center text-center -mt-2">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
            <i className="ri-calendar-todo-line text-2xl text-emerald-500" />
          </div>
          <h3 className="text-[15px] font-700 text-gray-800 mb-1">
            Nenhum evento em {MONTH_NAMES[month - 1]}
          </h3>
          <p className="text-[13px] text-gray-400 max-w-sm mb-5">
            Adicione lançamentos, metas e cartões para ver os eventos financeiros aqui. Você também pode criar lembretes pessoais.
          </p>
          <button
            onClick={handleAddNew}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-xl text-[13px] font-600 hover:bg-emerald-600 cursor-pointer transition-colors"
          >
            <i className="ri-add-line" />
            Criar primeiro evento
          </button>
        </div>
      )}

      {/* Action toast */}
      {actionToast && (
        <div className={`fixed bottom-5 right-5 z-[70] flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-500 shadow-lg pointer-events-none ${
          actionToast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <i className={`${actionToast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} text-base`} />
          {actionToast.msg}
        </div>
      )}

      {/* Modals */}
      <EventModal
        open={modalOpen}
        editEvent={editingEvent}
        defaultDate={selectedDate ?? undefined}
        onClose={() => { setModalOpen(false); setEditingEvent(null); }}
        onCreate={createEvent}
        onUpdate={updateEvent}
        onDelete={(id) => deleteEvent(id, 'single')}
      />

      <EventDetailModal
        open={detailOpen}
        event={detailEvent}
        googleConnected={gcalStatus.connected && perms.canAccessCalendarSync}
        onClose={() => { setDetailOpen(false); setDetailEvent(null); }}
        onEdit={handleEditFromDetail}
        onDelete={deleteEvent}
        onSync={perms.canAccessCalendarSync ? handleSync : undefined}
        onPayCard={handlePayCardFromCalendar}
        onGoalContribution={handleGoalContribution}
        onActionComplete={async () => {
          await refetchTransactions();
          setDetailOpen(false);
          setDetailEvent(null);
        }}
      />

      {/* Card payment modal (triggered from calendar) */}
      {calendarPaymentModal.open && (
        <CardPaymentModal
          open={calendarPaymentModal.open}
          card={cards.find(c => c.id === calendarPaymentModal.cardId) ?? null}
          stats={calendarPaymentStats}
          initialType={calendarPaymentModal.type}
          onClose={() => setCalendarPaymentModal(p => ({ ...p, open: false }))}
          onConfirm={handleCalendarPaymentConfirm}
        />
      )}

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        title="Calendário Avançado"
        description="Eventos manuais ilimitados, filtros avançados e sincronização com Google Calendar no plano Pro."
      />
    </div>
  );
};

export default CalendarioPage;
